import { html, LitElement, render } from 'lit';
import pulseTracker from '@finn-no/pulse-sdk';
import '@fabric-ds/icons/elements/messages-16';
import '@ungap/global-this';
import '@finn-no/require-eid-modal';

class FinnContactButton extends LitElement {
    static get properties() {
        // reason for change: https://newbedev.com/syntaxerror-unexpected-token-expected-a-or-a-after-a-parameter-declaration-in-safari
        return {
            buttonText: { type: String },
            isAdOwner: { type: Boolean },
            showError: { type: Boolean },
            buttonVariant: { type: String },
            size: { type: String },
            loginId: { type: Number },
            hasButtonIcon: { type: String },
            textAlignButton: { type: String },
            addVerticalMargin: { type: String },
            requireEid: { type: Boolean },
            eidVerified: { type: Boolean },
        };
    }

    constructor() {
        super();
        this.isAdOwner = false;
        this.buttonVariant = 'PRIMARY';
        this.size = 'DEFAULT';
        this.buttonText = 'Send melding';
        this.showError = false;
        this.loginId = '';
        this.adId = '';
        this.hasButtonIcon = 'false'; // values: true or false. true when the button has icon
        this.textAlignButton = 'CENTER'; // values: CENTER, START and END
        this.addVerticalMargin = 'false'; // values: true or false. true adds 10px margin on top and bottom
        this.requireEid = false;
        this.eidVerified = false;
        this.unknownError = false;
    }

    connectedCallback() {
        super.connectedCallback();
        // Fetch the meta data in the script tag rendered by the server
        const element = document.getElementById('contact-button-data');
        try {
            const data = JSON.parse(element.textContent);
            this.url = data.url;
            this.name = data.name;
            this.loginId = data.loginId;
            this.isAdOwner = data.isAdOwner;
            this.adId = data.adId;
            this.showError = data.showError;
            this.unknownError = data.unknownError;
            this.requireEid = data.requireEid;
            this.eidVerified = data.eidVerified;

            if (this.loginId && this.isAdOwner) {
                this.showError = JSON.parse(
                    window.localStorage.getItem('showError'),
                );
                window.localStorage.removeItem('showError');
            } else if (this.unknownError) {
                this.showError = JSON.parse(
                    window.localStorage.getItem('showError'),
                );
                window.localStorage.removeItem('showError');
            } else {
                this.showError = false;
                window.localStorage.setItem('showError', this.showError);
                window.localStorage.removeItem('showError');
            }
        } catch (error) {
            this.unknownError = true;
        }
    }

    getButtonClassNames() {
        let buttonClass = 'button--primary';
        let buttonTextAlignClass = 'justify-center';
        let addVerticalMargin;
        if (this.buttonVariant === 'SECONDARY') {
            buttonClass = 'button--secondary';
        }
        if (this.buttonVariant === 'FLAT') {
            buttonClass = 'button--flat';
        }
        if (this.textAlignButton === 'START') {
            buttonTextAlignClass = 'justify-start';
        }
        if (this.textAlignButton === 'END') {
            buttonTextAlignClass = 'justify-end';
        }
        if (this.addVerticalMargin === 'true') {
            addVerticalMargin = 'my-10';
        }
        if (this.size === 'SMALL') {
            buttonClass += ' button--small';
        }
        return `button flex items-center ${buttonClass} ${buttonTextAlignClass} ${addVerticalMargin} w-full max-w-none`;
    }

    iconTemplate() {
        let iconTemplate;
        if (this.hasButtonIcon === 'true') {
            iconTemplate = html`
                <f-icon-messages16 class="contents"></f-icon-messages16>
            `;
        }
        return iconTemplate;
    }

    buttonTemplate() {
        return html`
            <button
                title=${this.unknownError ? 'En feil har oppstått' : ''}
                class="${this.getButtonClassNames()}"
                @click="${this.errorCheck}"
            >
                ${this.iconTemplate()}
                <p class="m-0 text-16 mx-12">${this.buttonText}</p>
            </button>
        `;
    }

    linkTemplate() {
        if (this.requireEid && !this.eidVerified) {
            return html`
                <button
                    class="${this.getButtonClassNames()}"
                    @click="${this.openVerificationModal}"
                >
                    ${this.iconTemplate()}
                    <p class="m-0 text-16 mx-12">${this.buttonText}</p>
                </button>
                <div id="eid-modal-wrapper">
                    <require-eid-modal
                        id="eid-modal"
                        should-open="false"
                        eid-verification-url="${this.url}"
                    ></require-eid-modal>
                </div>
            `;
        }
        return html`
            <a
                rel="nofollow"
                href="${this.url}"
                class="${this.getButtonClassNames()}"
                @click="${this.trackButtonClick}"
            >
                ${this.iconTemplate()}
                <p class="m-0 text-16 mx-12">${this.buttonText}</p>
            </a>
        `;
    }

    errorTemplate() {
        let errorTemplate;
        if (this.showError && this.isAdOwner) {
            errorTemplate = html`
                <p
                    class="text-red-500 text-center my-4 text-12"
                    id="send-message-own-ad-error-msg"
                >
                    Oi sann! Du kan ikke sende melding til deg selv.
                </p>
            `;
        } else if (this.showError) {
            errorTemplate = html`
                <p
                    class="text-red-500 text-center my-4 text-12"
                    id="send-message-own-ad-error-msg"
                >
                    Oi sann! Nå gikk noe galt.
                </p>
            `;
        }
        return errorTemplate;
    }

    getFinalTemplate() {
        if (this.isAdOwner || this.unknownError) {
            return html`${this.buttonTemplate()}${this.errorTemplate()}`;
        }
        return html`${this.linkTemplate()}`;
    }

    render() {
        return html`
            <link
                href="https://assets.finn.no/pkg/@fabric-ds/css/v1/fabric.min.css"
                rel="stylesheet"
                type="text/css"
            />
            <div>${this.getFinalTemplate()}</div>
        `;
    }

    errorCheck() {
        this.showError = true;
    }

    trackButtonClick() {
        if (!this.loginId) {
            window.localStorage.setItem('showError', true);
        }
        // Calling the tracker is not required when the user is the ad owner.
        pulseTracker.trackEvent({
            type: 'Click',
            intent: 'Create',
            name: 'Send message',
            object: {
                id: 'sendMessageButton',
                type: 'UIElement',
                elementType: 'Button',
                items: [
                    {
                        type: 'ClassifiedAd',
                        id: this.adId,
                        contentId: this.adId,
                        name: 'n/a',
                        category: 'n/a',
                        hasExtendedProfile: 'false',
                    },
                ],
                page: {
                    type: 'ClassifiedAd',
                },
            },
            target: {
                id: 'id',
                type: 'Message',
            },
        });
    }

    openVerificationModal() {
        const modal = this.shadowRoot.getElementById('eid-modal');
        const parent = this.shadowRoot.getElementById('eid-modal-wrapper');
        modal.setAttribute('should-open', 'true');
        render(modal, parent);
        if (document.getElementById('rightPaneContainer') !== null) {
            document.getElementById('rightPaneContainer').style.zIndex = '3'; // parent container 'rightPaneContainer' is in mFinn
        }
        document.addEventListener('onModalClose', () => {
            const renderedModal = this.shadowRoot.getElementById('eid-modal');
            const renderedParent =
                this.shadowRoot.getElementById('eid-modal-wrapper');
            if (renderedModal) {
                renderedModal.setAttribute('should-open', 'false');
                render(renderedModal, renderedParent);
                if (document.getElementById('rightPaneContainer') !== null) {
                    document.getElementById('rightPaneContainer').style.zIndex =
                        '0';
                }
            }
        });

        this.trackButtonClick();
    }
}

customElements.define('finn-contact-button', FinnContactButton);
